import React, { useEffect, useState } from "react";
import AdminAppBar from "../../adminComponents/AdminAppBar";
import { toast, ToastContainer } from "react-toastify";
import BackArrows from "../../Form/BackArrow/BackArrows";
import { showErrorToast, showSuccessToast } from "../../Constant/Notification";
import AmenitiesService from "../../Services/AmenitiesService";
import { useNavigate } from "react-router-dom";
import { MdEdit } from "react-icons/md";
import CustomHeading from "../../common/CustomHeading";

const PropertyInventory = () => {
  const navigator = useNavigate();
  const [loading, setLoading] = useState(false);
  const [amenities, setAmenities] = useState({
    basic: [],
    feature: [],
    nearby: [],
  });
  const [limits, setLimits] = useState({
    basic: 250,
    feature: 250,
    nearby: 250,
  });

  const fetchAmenities = async () => {
    try {
      const response = await AmenitiesService.getAmentities();
      const data = response.data || [];
      setAmenities({
        basic: data.filter((item) => item.type === 1),
        feature: data.filter((item) => item.type === 2),
        nearby: data.filter((item) => item.type === 3),
      });
    } catch (error) {
      console.error("Error fetching amenities:", error);
    }
  };

  useEffect(() => {
    fetchAmenities();
  }, []);

  const handleBack = () => {
    navigator(-1);
  };

  return (
    <div className=" min-h-screen">
      <AdminAppBar />
      <ToastContainer />
      <div className="pt-28 pb-12">
        <div className="px-6 ">
          <div className="flex items-center justify-between">
           <CustomHeading title="Property Inventory" isIcon={true} />
          </div>
        </div>
        <div className="mt-5 mx-8 bg-white rounded-md shadow-md p-6 ">
          <AmenitiesSection
            title="Basic Amenities"
            amenities={amenities.basic}
            type={1}
            limit={limits.basic}
            fetchAmenities={fetchAmenities}
            setAmenities={(updated) =>
              setAmenities((prev) => ({ ...prev, basic: updated }))
            }
            note="Add essential amenities available within the property, e.g., Wi-Fi, Air Conditioning, Kitchen, Parking."
          />
          <AmenitiesSection
            title="Feature Amenities"
            amenities={amenities.feature}
            type={2}
            limit={limits.feature}
            fetchAmenities={fetchAmenities}
            setAmenities={(updated) =>
              setAmenities((prev) => ({ ...prev, feature: updated }))
            }
            note="Add special features that enhance the property, e.g., Swimming Pool, Gym, Balcony, Smart Home System."
          />
          <AmenitiesSection
            title="Nearby Amenities"
            amenities={amenities.nearby}
            type={3}
            limit={limits.nearby}
            fetchAmenities={fetchAmenities}
            setAmenities={(updated) =>
              setAmenities((prev) => ({ ...prev, nearby: updated }))
            }
            note="Add amenities located near the property, e.g., Supermarket, Public Transport, Park, Hospital."
          />
        </div>
      </div>
    </div>
  );
};

const AmenitiesSection = ({
  title,
  amenities,
  type,
  limit,
  note,
  setAmenities,
  fetchAmenities,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [editingAmenity, setEditingAmenity] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleAddOrUpdate = async () => {
    const trimmedValue = inputValue.trim();
    if (!trimmedValue) {
      showErrorToast("Amenity name cannot be empty");
      return;
    }

    if (trimmedValue.length > 50) {
      showErrorToast("Amenity name cannot exceed 50 characters");
      return;
    }

    const isDuplicate = amenities.some(
      (item) =>
        item.name.toLowerCase() === trimmedValue.toLowerCase() &&
        item.id !== editingAmenity?.id
    );
    if (isDuplicate) {
      showErrorToast("This amenity already exists");
      return;
    }
    setIsLoading(true);
    try {
      if (editingAmenity) {
        const data = {
          id: editingAmenity.id,
          name: trimmedValue,
          type,
        };
        await AmenitiesService.updateAmenities(data);
        setAmenities(
          amenities.map((item) =>
            item.id === editingAmenity.id
              ? { ...item, name: trimmedValue }
              : item
          )
        );
        showSuccessToast("Amenity updated successfully");
      } else {
        if (amenities.length >= limit) {
          showErrorToast(`Maximum limit of ${limit} amenities reached`);
          return;
        }
        const response = await AmenitiesService.createAmenities({
          name: trimmedValue,
          type,
        });
        setAmenities([...amenities, response.data]);
        showSuccessToast("Amenity created successfully");
      }
      setInputValue("");
      setEditingAmenity(null);
      fetchAmenities();
    } catch (error) {
      showErrorToast(error.response?.data || "Operation failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (amenity) => {
    setEditingAmenity(amenity);
    setInputValue(amenity.name);
  };

  const handleCancel = () => {
    setInputValue("");
    setEditingAmenity(null);
  };

  return (
    <div className="mb-8">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">{title}</h1>
        <span
          className={`text-sm ${
            amenities.length >= limit * 0.8 ? "text-red-500" : "text-gray-500"
          }`}
        >
          {amenities.length}/{limit} {title} used
        </span>
      </div>
      <div className="flex gap-16 mt-4">
        <div className="custom-scrollbar overflow-x-hidden flex flex-wrap flex-auto w-7/12 h-64 rounded-md p-3 gap-x-2 gap-y-2 bg-appGrayBackground overflow-y-auto">
          {amenities.map((item) => (
            <div
              key={item.id}
              className="bg-customGreen h-8 rounded-lg px-3 text-sm font-normal text-white mr-2 mb-2 flex items-center"
            >
              <span>{item.name}</span>
              <button
                className="ml-2 focus:outline-none"
                onClick={() => handleEdit(item)}
                disabled={amenities.length >= limit && !editingAmenity}
              >
                <MdEdit className="w-4 h-4 text-white" />
              </button>
            </div>
          ))}
        </div>
        <div className="flex flex-col w-5/12">
          <div className="flex gap-4 items-start">
            <div className="flex flex-col gap-1 max-w-md w-full">
              <input
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                maxLength={50}
                placeholder={`Enter ${title.toLowerCase()}`}
                className="px-4 py-2 h-10 text-sm rounded-md border border-gray-300 input-focus"
                disabled={amenities.length >= limit && !editingAmenity}
              />
              <div className="text-sm text-gray-500 mt-2">{note}</div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleAddOrUpdate}
                disabled={
                  (amenities.length >= limit && !editingAmenity) || isLoading
                }
                className={`h-10 px-4 text-sm font-medium rounded-md transition duration-200 ${
                  isLoading
                    ? "text-gray-400 bg-gray-200 cursor-not-allowed"
                    : amenities.length >= limit && !editingAmenity
                    ? "text-red-400 bg-red-100 cursor-not-allowed"
                    : "text-white bg-customGreen"
                }`}
              >
                {isLoading ? "Loading..." : editingAmenity ? "Update" : "Add"}
              </button>

              {editingAmenity && (
                <button
                  onClick={handleCancel}
                  className="h-10 px-4 text-sm font-medium rounded-md transition duration-200 text-white bg-gray-500 hover:bg-gray-600"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyInventory;
